import React, { useState, useEffect } from 'react';
import '../../App.css';
import '../data-management/data-management.css';
import '../../tools/search-results/search-results.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { useTranslation } from 'react-i18next';

// icons
import fileLargeIcon from '../../icons/file.128.png';
import fileSmallIcon from '../../icons/file-small.128.png';
import fileDetailsIcon from '../../icons/file-details.128.png';
import imageIcon from '../../icons/image.256.png';
import catalogIcon from '../../icons/catalog.256.png';
import fileIcon from '../../icons/file.512.png';
import refreshIcon from '../../icons/refresh.512.png';

// types
import { Event, EventType, EventTarget, GatewaySettingsType } from '../../utils/events';

// import responsive layout items.
import { Col, Row } from 'react-bootstrap';

// functions
import { siteName } from '../../utils/functions';
import { brw_handleItemClick, brw_keyDownHandler } from '../../utils/item-browser';

// classes
import Scrollbox from '../../tools/scrollbox';
import PleaseWait from '../../tools/please-wait';
import MiniButton from '../../tools/mini-button';

// types
import { DataItemType, RucioLocationsType, Site } from '../../utils/types';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

import { store } from '../../app/store';

// import state definition and functions (redux).
import {
  ProjectState,
  addToSelectedDivs,
  removeFromSelectedDivs,
  setContextMenuRefresh,
  setFilenameFilter,
  setFocusElement,
  setSelectedItems,
  setSelectedDivs,
} from './project-slice';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_MENU_DATA_STAGING as CONTEXT_MENU,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import {
  ctx_buildContextMenu,
  ctx_calculatePosition,
  ctx_keyDownHandler,
} from '../menus/context-menu-actions';
import { SettingsState } from '../../app/settings-slice';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function ProjectFilesTable() {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.project.loadingComponent,
  );

  // the data items displayed in the table.
  const sProjectItems: DataItemType[] = useAppSelector(
    (state) => state.project.projectItems,
  );

  // filter values.
  const sFilenameFilter: string = useAppSelector((state) => state.project.filenameFilter);

  // global slice.
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // selected items.
  const sSelectedItems: DataItemType[] = useAppSelector(
    (state) => state.project.selectedItems,
  );
  const sSelectedDivs: number[] = useAppSelector((state) => state.project.selectedDivs);
  const sFocusElement: { divID: number } = useAppSelector(
    (state) => state.project.focusElement,
  );

  // data
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  );

  const sErrorMessage: string = useAppSelector((state) => state.project.errorMessage);

  // gateway settings.
  const sIconSize: string | undefined = useAppSelector(
    (state) => state.settings.iconSize,
  );

  // context menu refresh.
  const sContextMenuRefresh: number = useAppSelector(
    (state) => state.project.contextMenuRefresh,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // job just been submitted?
  const [sSubmittingJob, setSubmittingJob] = useState<boolean>(false);

  const [sKeyDownTrigger, setKeyDownTrigger] = useState<{
    value: number;
    keyCode: number;
  }>({ value: 0, keyCode: 0 });

  //	--------------------------------------------------------------------------
  //
  //	C O N T E X T   M E N U   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const CONTEXT_SYSTEM: CONTEXT_SYSTEMS = CONTEXT_SYSTEMS.PROJECT_DATA_FILES;

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Set the focus to a specific item in the project-data browser.
  //
  //	------------------------------------------------------------

  function focusDataItem() {
    //
    const stateFetch: any = store.getState();
    const state: ProjectState = stateFetch.project;

    window.requestAnimationFrame(() => {
      const target = document.getElementById(
        `itmProjectData_${state.focusElement.divID}`,
      );
      if (target instanceof HTMLElement) {
        target.focus();
      }
    });
    //
  } // focusDataItem

  //	------------------------------------------------------------
  //
  //	Work out how many items stretch across the row in the
  //	browser.
  //
  //	------------------------------------------------------------

  const getItemsPerRow = () => {
    //
    const stateFetch: any = store.getState();
    const stateSettings: SettingsState = stateFetch.settings;

    if (stateSettings.iconSize === 'list') {
      return 1;
    }
    const container = document.getElementById('container');
    const sample =
      container?.querySelector<HTMLDivElement>('.data-management-item') ?? null;
    if (!container || !sample) {
      return 1;
    }
    const width = sample.getBoundingClientRect().width || 1;

    return Math.max(1, Math.floor(container.clientWidth / width));
    //
  }; // getItemsPerRow

  //	------------------------------------------------------------
  //
  //	builds a list of the selected items.
  //
  //	------------------------------------------------------------

  function getSelectedItems(args: { divID: number[] }) {
    //
    var selectedItems: DataItemType[] = [];

    // loop over the selected IDs.
    for (var selectedItemIdx = 0; selectedItemIdx < args.divID.length; selectedItemIdx++)
      selectedItems.push(sProjectItems[args.divID[selectedItemIdx]]);

    // return something.
    return selectedItems;
    //
  } // getSelectedItems

  //	------------------------------------------------------------
  //
  //	Function that clears all selected itest and resets the
  //  context menu state when an empty area of the container is clicked
  //
  //	------------------------------------------------------------

  function handleContainerDeselection() {
    //
    dispatch(setSelectedDivs([]));
    dispatch(
      ctx_setContextRow({
        contextSystem: CONTEXT_SYSTEM,
        row: -1,
      }),
    );
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEM,
        visible: false,
      }),
    );
    //
  } // handleContainerDeselection

  //	------------------------------------------------------------
  //
  //	Displays the right-click context menu
  //
  //	------------------------------------------------------------

  function handleRightClickContextMenu(args: {
    event: React.MouseEvent<HTMLDivElement>;
    divElement: HTMLDivElement;
  }) {
    //
    const scrollDiv = document.querySelector<HTMLDivElement>(
      '#data-management-scrollbox',
    );
    const scrollPos = scrollDiv?.scrollTop ?? 0;

    // update the context-menu definition.
    ctx_buildContextMenu({
      contextSystem: CONTEXT_SYSTEM,
      contextMenu: CONTEXT_MENU.MAIN,
      parentRowID: -1,
    });

    // calculate the context-menu position.
    ctx_calculatePosition({
      contextSystem: CONTEXT_SYSTEM,
      contextMenu: CONTEXT_MENU.MAIN,
      pos: {
        x: args.event.clientX,
        y: args.event.clientY,
      },
    });

    // set only the first menu to be visible.
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEM,
        contextMenu: CONTEXT_MENU.MAIN,
        visible: true,
      }),
    );

    // on the right-click context menu we only want to stage items that are selected.
    dispatch(setContextMenuRefresh(Date.now()));
    //
  } // handleRightClickContextMenu

  //	------------------------------------------------------------
  //
  //	Handles clicks on toolbar buttons and contextual UI elements
  //	tied to the File Browser
  //
  //	------------------------------------------------------------

  function handleToolbarActions(event: React.MouseEvent<HTMLButtonElement>) {
    //
    const id: string = event.currentTarget.id;

    switch (id) {
      //
      case 'icon-size-large':
      case 'icon-size-medium':
      case 'icon-size-details': {
        const params: GatewaySettingsType = {
          iconSize:
            id === 'icon-size-details'
              ? 'list'
              : id === 'icon-size-large'
                ? 'large'
                : 'medium',
        };
        const newEvent: Event = {
          eventTarget: EventTarget.APP,
          eventType: EventType.SET_PREFERENCES,
          parameters: params,
        };
        dispatch(handleEvent({ event: newEvent }));
        break;
      }

      case 'refresh-page':
        refreshPage();
        break;
      //
    }
    //
  } // handleToolbarActions

  //	------------------------------------------------------------
  //
  //	Handler for lost-focus events on the input boxes.
  //
  //	------------------------------------------------------------

  const onBlurEvent = (event: React.FocusEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    // update the state variable that holds the current value of the filename.
    if (inputBox.name === 'filename') {
      //
      // run the project-files search.
      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_PROJECT_FILES_SEARCH,
      };
      dispatch(handleEvent({ event: newEvent }));
      //
    }
    //
  }; // onBlurEvent

  //	------------------------------------------------------------
  //
  //	Handler for changes to the input boxes.
  //
  //	------------------------------------------------------------

  const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    // update the state for any input boxes that have been changed.
    if (inputBox.name === 'filename') {
      dispatch(setFilenameFilter(inputBox.value));
    }
    //
  }; // onChangeEvent

  //	------------------------------------------------------------
  //
  //	Handler button clicks.
  //
  //	------------------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    // Handle toolbar actions (e.g. icon size, staging, etc.)
    handleToolbarActions(event);

    // prevent propogation of the onClick event.
    event.stopPropagation();
    //
  }; // onClickBtnHandler

  //	------------------------------------------------------------
  //
  //	Handler for different clicks logic.
  //
  //	------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const stateFetch: any = store.getState();
    const state: ProjectState = stateFetch.project;

    const divElement = event.currentTarget;
    const rightClick = event.type === 'contextmenu';
    const isClickOnEmptyContainer: boolean =
      event.target instanceof HTMLDivElement && event.target.id === 'container';

    // Handle clicking on empty container
    if (isClickOnEmptyContainer) {
      handleContainerDeselection();
    }

    // Handle item click. The event on the selected
    // item fires first, followed by the event on the container.
    if (divElement.id.startsWith('itmProjectData_')) {
      //
      const divID = Number(divElement.id.slice(15));
      if (isNaN(divID) === false) {
        //
        brw_handleItemClick({
          divID: divID,
          shiftKey: event.shiftKey,
          ctrlKey: event.ctrlKey,
          selectedDivs: state.selectedDivs,
          setSelectedDivs: setSelectedDivs,
          addToSelectedDivs: addToSelectedDivs,
          removeFromSelectedDivs: removeFromSelectedDivs,
          focusElement: state.focusElement.divID,
          setFocusElement: setFocusElement,
        });
        //
      }
      //
    }

    // Handle right-click context menu
    if (rightClick && divElement.id === 'container') {
      handleRightClickContextMenu({
        event: event,
        divElement: divElement,
      });
      event.preventDefault();
    }
    //
  }; // onClickHandler

  //	------------------------------------------------------------
  //
  //	Handler for when a key is pressed on a data item.
  //
  //	------------------------------------------------------------

  function onKeyDownHandler(event: React.KeyboardEvent<HTMLElement>) {
    //
    const stateFetch: any = store.getState();
    const state: ProjectState = stateFetch.project;

    // first, see if the context menu handler wants to process this key press.
    const handled: boolean = ctx_keyDownHandler({ key: event.key });

    // the event was handled by the context-menu handler. stop the propagation.
    if (handled === true) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // otherwise, close any context menu.
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEM,
        visible: false,
      }),
    );

    // count the number of items across each row.
    const itemsPerRow = getItemsPerRow();

    // check if the item browser handler wishes to handle this key down event.
    // this handler moves the focus from one item to another, and handles
    // the arrow keys being pressed in conjunction with the shift key.
    if (
      brw_keyDownHandler({
        event: event,
        dataItems: state.projectItems.length,
        itemsPerRow: itemsPerRow,
        selectedDivs: state.selectedDivs,
        setSelectedDivs: setSelectedDivs,
        addToSelectedDivs: addToSelectedDivs,
        removeFromSelectedDivs: removeFromSelectedDivs,
        focusElement: state.focusElement.divID,
        setFocusElement: setFocusElement,
      }) === true
    ) {
      //
      // stop the propagation.
      event.preventDefault();
      event.stopPropagation();
      //
    }
    //
  } // onKeyDownHandler

  //	------------------------------------------------------------
  //
  //	refresh the page
  //
  //	------------------------------------------------------------

  function refreshPage() {
    //
    // run the project-files search.
    const newEvent: Event = {
      eventTarget: EventTarget.PROJECT_FILES,
      eventType: EventType.INITIATE_PROJECT_FILES_SEARCH,
    };
    dispatch(handleEvent({ event: newEvent }));
    //
  } // refreshPage

  //	------------------------------------------------------------
  //
  //	H O O K S
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	set event handlers when this component loads.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[] hook fires (data-management-table)');

    const handleKeyDown = (event: KeyboardEvent) => {
      setKeyDownTrigger({ value: Date.now(), keyCode: event.keyCode });
      console.log(event.keyCode);
    };

    document.addEventListener('keydown', handleKeyDown);

    // Don't forget to clean up
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    };
    //
  }, []);

  //  ------------------------------------------------------------
  //
  //  if the focused element in the file browser is changed,
  //  then we need to set the focus here.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    if (sFocusElement.divID > -1) {
      focusDataItem();
    }
    //
  }, [sFocusElement]);

  //	------------------------------------------------------------
  //
  //	update the selected items when the selected divs change.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    dispatch(setSelectedItems(getSelectedItems({ divID: sSelectedDivs })));
    //
  }, [sSelectedDivs]);

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <Row
      className="panel-main"
      style={{
        flexDirection: 'column',
        margin: '0px',
        flex: '1 1',
      }}
    >
      <div
        style={{
          flex: '1 1',
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          padding: '0px',
        }}
      >
        {/*
         //  Column to contain the main window. handle key presses on any data elements within.
         // */}
        <div
          style={{
            flex: '1 1',
            display: 'flex',
            flexDirection: 'column',
            marginRight: '2px',
          }}
          onKeyDown={onKeyDownHandler}
        >
          {sG_loggedIn !== true && (
            <div className="no-data-overlay">
              <span>Login required</span>
            </div>
          )}
          {sG_loggedIn === true && sG_project === undefined && (
            <div className="no-data-overlay">
              <span>No project selected</span>
            </div>
          )}
          {/*
           //  The data-items window, with a vertical scrollbar if required.
           // */}
          <Scrollbox
            id="data-management-scrollbox"
            direction="vertical"
            width="100%"
            flex="1 1"
            flexDirection="column"
            padding="10px 10px 10px 10px"
          >
            {/* display the list of data items */}
            <DataItems
              key={
                sProjectItems.length.toString() +
                sSelectedDivs.length.toString() +
                sFocusElement.divID.toString()
              }
              errorMessage={sErrorMessage}
              onClickHandler={onClickHandler}
              contextMenuRefresh={sContextMenuRefresh}
              focusIndex={sFocusElement.divID}
            />
          </Scrollbox>
          <div style={{ flex: '0 0 auto', marginLeft: '10px' }}>
            <PleaseWait
              text={t('Please wait') + '.....'}
              visible={sLoadingComponent === true}
            />
          </div>
        </div>
      </div>

      <div className="fb-footer">
        <div style={{ flex: '1 1' }}></div>

        <div className="fb-footer__filter">
          <input
            className="input-field"
            type="text"
            name="filename"
            placeholder={t('Filter names')}
            onBlur={onBlurEvent}
            onChange={onChangeEvent}
            value={sFilenameFilter}
            maxLength={255}
          />
        </div>

        <div className="fb-footer__icons">
          <MiniButton
            id="icon-size-details"
            className="fb-footer__icon"
            data-selected={sIconSize === 'list' ? 'T' : 'F'}
            onClick={onClickBtnHandler}
          >
            <img
              src={fileDetailsIcon}
              alt="Detailed list"
              width="34"
              height="34"
              title={t('Detailed list')}
            />
          </MiniButton>

          <MiniButton
            id="icon-size-medium"
            className="fb-footer__icon"
            data-selected={sIconSize === 'medium' || sIconSize === undefined ? 'T' : 'F'}
            onClick={onClickBtnHandler}
          >
            <img
              src={fileSmallIcon}
              alt="Small icons"
              width="34"
              height="34"
              title={t('Small icons')}
            />
          </MiniButton>

          <MiniButton
            id="icon-size-large"
            className="fb-footer__icon"
            data-selected={sIconSize === 'large' ? 'T' : 'F'}
            onClick={onClickBtnHandler}
          >
            <img
              src={fileLargeIcon}
              alt="Large icons"
              width="34"
              height="34"
              title={t('Large icons')}
            />
          </MiniButton>

          <MiniButton
            id="refresh-page"
            className="fb-footer__icon"
            onClick={onClickBtnHandler}
          >
            <img
              src={refreshIcon}
              alt="Refresh"
              width="34"
              height="34"
              title={t('Refresh')}
            />
          </MiniButton>
        </div>
      </div>
    </Row>
  );
  //
} // ProjectFilesTable

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Display a small sized icon for each item, with a single
//	row of details.
//
//	------------------------------------------------------------

function DataItemDetails(props: {
  id: string;
  item: DataItemType;
  itemSelected: boolean;
  itemFocused: boolean;
  onClickHandler: any;
}) {
  //
  // translation function
  const { t } = useTranslation();

  //	------------------------------------------------------------
  //
  //	handle onClick events for the input boxes, so that we can
  //	stop propagation.
  //
  //	------------------------------------------------------------

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  }; // onClick

  return (
    <div
      className="data-management-item"
      style={{
        width: '100%',
        margin: '2px 2px 2px 2px',
        float: 'left',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
      id={props.id}
      data-selected={props.itemSelected ? 'T' : 'F'}
      onClick={props.onClickHandler}
      onContextMenu={props.onClickHandler}
      tabIndex={props.itemFocused === true ? 0 : -1}
      role="option"
      aria-selected={props.itemSelected ? 'true' : 'false'}
    >
      <div
        style={{
          flex: '0 0 42px',
          display: 'flex',
          flexDirection: 'row',
          margin: '0px 0px 0px 0px',
          alignItems: 'left',
        }}
      >
        <div style={{ flex: '0 0 10px' }} />
        <Icon item={props.item} size={40} />
        <div style={{ flex: '0 0 10px' }} />
        <div
          className="text-colour"
          data-selected={props.itemSelected ? 'T' : 'F'}
          style={{
            whiteSpace: 'nowrap',
            flex: '1 1',
            alignItems: 'center',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          {props.item.namespace + ':' + props.item.filename}
        </div>
        <div style={{ flex: '0 0 10px' }} />
        <div
          className="text-colour"
          data-selected={props.itemSelected ? 'T' : 'F'}
          style={{
            whiteSpace: 'nowrap',
            flex: '0 0 120px',
            alignItems: 'center',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          {props.item.did_type ?? ''}
        </div>
      </div>
    </div>
  );
  //
} // DataItemDetails

//	------------------------------------------------------------
//
//	Display a medium sized icon for each item.
//
//	------------------------------------------------------------

function DataItemMedium(props: {
  id: string;
  item: DataItemType;
  itemSelected: boolean;
  itemFocused: boolean;
  onClickHandler: any;
}) {
  //
  // translation function
  const { t } = useTranslation();

  //	------------------------------------------------------------
  //
  //	handle onClick events for the input boxes, so that we can
  //	stop propagation.
  //
  //	------------------------------------------------------------

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  }; // onClick

  return (
    <div
      className="data-management-item"
      style={{
        width: '375px',
        margin: '2px 2px 2px 2px',
        float: 'left',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
      id={props.id}
      data-selected={props.itemSelected === true ? 'T' : 'F'}
      onClick={props.onClickHandler}
      onContextMenu={props.onClickHandler}
      tabIndex={props.itemFocused === true ? 0 : -1}
      role="option"
      aria-selected={props.itemSelected ? 'true' : 'false'}
    >
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'row',
          margin: '0px 0px 0px 0px',
          alignItems: 'left',
        }}
      >
        <div style={{ flex: '0 0 10px' }} />
        <Icon item={props.item} size={40} />
        <div
          className="text-colour"
          data-selected={props.itemSelected ? 'T' : 'F'}
          style={{
            flex: '1 1',
            display: 'flex',
            flexDirection: 'column',
            margin: '0px 5px 0px 5px',
            height: '42px',
          }}
        >
          <div style={{ flex: '1 1' }} />
          <div
            style={{
              overflowWrap: 'anywhere',
              flex: '0 0 auto',
              alignItems: 'center',
              textAlign: 'left',
            }}
          >
            {props.item['namespace'] + ':' + props.item.filename}
          </div>
          <div style={{ flex: '1 1' }} />
        </div>
      </div>
    </div>
  );
  //
} // DataItemMedium

//	------------------------------------------------------------
//
//	Display a large sized icon for each item.
//
//	------------------------------------------------------------

function DataItemLarge(props: {
  id: string;
  item: DataItemType;
  itemSelected: boolean;
  itemFocused: boolean;
  onClickHandler: any;
}) {
  //
  // translation function
  const { t } = useTranslation();

  //	------------------------------------------------------------
  //
  //	handle onClick events for the input boxes, so that we can
  //	stop propagation.
  //
  //	------------------------------------------------------------

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  }; // onClick

  return (
    <div
      className="data-management-item"
      style={{
        width: '245px',
        height: '160px',
        margin: '2px 2px 2px 2px',
        float: 'left',
        display: 'flex',
        flexDirection: 'column',
      }}
      id={props.id}
      data-selected={props.itemSelected === true ? 'T' : 'F'}
      onClick={props.onClickHandler}
      onContextMenu={props.onClickHandler}
      tabIndex={props.itemFocused === true ? 0 : -1}
      role="option"
      aria-selected={props.itemSelected ? 'true' : 'false'}
    >
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          margin: '0px 0px 0px 0px',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <div style={{ flex: '0 0 10px' }} />
        <Icon item={props.item} size={90} />
        <div
          className="text-colour"
          data-selected={props.itemSelected ? 'T' : 'F'}
          style={{
            overflowWrap: 'anywhere',
            flex: '0 0 42px',
            display: 'flex',
            alignItems: 'center',
            margin: '0px 5px 0px 5px',
            textAlign: 'center',
            boxSizing: 'border-box',
          }}
        >
          {props.item.namespace + ':' + props.item.filename}
        </div>
        <div style={{ flex: '0 0 10px' }} />
      </div>
    </div>
  );
  //
} // DataItemLarge

//	------------------------------------------------------------
//
//	Display an icon and name for each data item.
//
//	------------------------------------------------------------

function DataItems(props: {
  errorMessage: string;
  onClickHandler: any;
  contextMenuRefresh: number;
  focusIndex: number;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // gateway settings.
  const sIconSize: string | undefined = useAppSelector(
    (state) => state.settings.iconSize,
  );

  // the data items displayed in the table.
  const sProjectItems: DataItemType[] = useAppSelector(
    (state) => state.project.projectItems,
  );

  // selected divs.
  const sSelectedDivs: number[] = useAppSelector((state) => state.project.selectedDivs);

  //	------------------------------------------------------------
  //
  //	D E C L A R A T I O N
  //
  //	------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //	------------------------------------------------------------
  //
  //	retrieves the scroll position of the container that
  //	holds the context menu.
  //
  //	------------------------------------------------------------

  function getContainerScrollPosition() {
    var containerScrollPosition: number = 0.0;

    // get the DIV element with the vertical scroll bars, and find the scroll position.
    const id = 'data-management-scrollbox';
    const scrollDiv: HTMLDivElement | null = document.querySelector(`#${id}`);
    if (scrollDiv !== null) containerScrollPosition = scrollDiv.scrollTop;

    // return something.
    return containerScrollPosition;
  } // getContainerScrollPosition

  const hasNoItems = Object.keys(sProjectItems).length === 0;

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <div
      style={{
        flex: '1 1',
        width: '100%',
        textAlign: 'left',
        position: 'relative',
        fontWeight: '400',
      }}
      id="container"
      onClick={props.onClickHandler}
      onContextMenu={props.onClickHandler}
    >
      {hasNoItems ? (
        <div className="no-sessions-overlay">
          <span>{props.errorMessage}</span>
        </div>
      ) : (
        sProjectItems.map((item: DataItemType, index: number) =>
          sIconSize === 'list' ? (
            <DataItemDetails
              key={index}
              id={'itmProjectData_' + index.toString()}
              item={item}
              itemSelected={sSelectedDivs.indexOf(index) > -1}
              itemFocused={props.focusIndex === index}
              onClickHandler={props.onClickHandler}
            />
          ) : sIconSize === 'medium' || sIconSize === undefined ? (
            <DataItemMedium
              key={index}
              id={'itmProjectData_' + index.toString()}
              item={item}
              itemSelected={sSelectedDivs.indexOf(index) > -1}
              itemFocused={props.focusIndex === index}
              onClickHandler={props.onClickHandler}
            />
          ) : (
            <DataItemLarge
              key={index}
              id={'itmProjectData_' + index.toString()}
              item={item}
              itemSelected={sSelectedDivs.indexOf(index) > -1}
              itemFocused={props.focusIndex === index}
              onClickHandler={props.onClickHandler}
            />
          ),
        )
      )}
    </div>
  );
} // DataItems

//	------------------------------------------------------------
//
//	Display icon
//
//	------------------------------------------------------------

function Icon(props: { item: DataItemType; size: number }) {
  //
  const emptyIconStyle = {
    textAlign: 'center',
    width: props.size.toString() + 'px',
    height: props.size.toString() + 'px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '7pt',
  } as React.CSSProperties;

  if ((props.item.did_type ?? '') !== '') {
    switch ((props.item.did_type ?? '').toUpperCase()) {
      case 'IMAGE':
        return <img src={imageIcon} alt="" width={props.size} height={props.size} />;
        break;
      case 'CATALOGUE':
        return <img src={catalogIcon} alt="" width={props.size} height={props.size} />;
        break;
      default:
        return <img src={fileIcon} alt="" width={props.size} height={props.size} />;
        /*return (
          <div className="border-colour" style={emptyIconStyle}>
            {props.item.did_type ?? ''}
          </div>
        );*/
        break;
    }
  } else {
    return <div className="border-colour" style={emptyIconStyle}></div>;
  }
  //
} // Icon
